import React, { useMemo } from 'react';
import KanbanFilterDropdown from './KanbanFilterDropdown/index';
import SortDropdown from './SortDropdown';
import { StatusLimitsDropdown } from './ColumnLimitsDropdown';
import FieldVisibilityDropdown from './FieldVisibilityDropdown';
import SwimlanesDropdown from './SwimlanesDropdown';
import FokusButton from '../shared/FokusButton';
import { calculateLockStates } from '../FlowchartView/utils/dependencyUtils';
import { isTaskActionable } from '../shared/utils/taskAvailabilityUtils';
import {
  ToolbarArchiveToggle,
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  SearchIcon,
  ResponsiveToolbar,
} from '../shared/ToolbarPrimitives';
import ResetCampaignToolbarItem from './ResetCampaignToolbarItem';
import VariablesToolbarItem from './VariablesToolbarItem';

/**
 * TableToolbar Component
 * 
 * Contains add mission/decision buttons, filters, and undo/redo.
 */
const TableToolbar = ({
  showArchived = false,
  onToggleArchived,
  archivedCount = 0,
  // Actionable only filter
  showActionableOnly = false,
  onToggleActionableOnly,
  // Filter props
  filters = {},
  onFiltersChange,
  statuses = [],
  categories = [],
  allTags = [],
  tasks = [],
  showNestedCampaigns = true,
  onShowNestedCampaignsChange,
  nestedSprints = [],
  // Sort props
  sortSetting,
  onSortSettingChange,
  // WIP limits
  onSetWipLimit,
  // FOKUS mode props
  isFokusMode = false,
  selectedTaskIds = new Set(),
  onFokusSelected,
  onExitFokusMode,
  onFokusByFilter,
  // Column visibility props
  columnVisibility = {},
  onColumnVisibilityChange,
  // Custom fields (campaign-level definitions)
  customFields = [],
  onManageCustomFields,
  // Swimlane settings (same as Kanban - 'none', 'tag', 'priority', etc.)
  swimlaneSetting = 'none',
  onSwimlaneSettingChange,
  swimlaneContext = {},
  // Add task/decision handlers
  onAddTask,
  onAddDecision,
  onAddWait,
  // Mission search
  onOpenMissionSearch,
  onResetCampaignProgress,
  onManageVariables = null,
}) => {
  // Calculate lock states for mission availability filtering
  const lockStates = useMemo(() => {
    return calculateLockStates(tasks, nestedSprints, statuses);
  }, [tasks, nestedSprints, statuses]);

  // Calculate actionable task IDs for FOKUS mode
  const actionableTaskIds = useMemo(() => {
    return tasks
      .filter(task => isTaskActionable(task, lockStates, nestedSprints, statuses))
      .map(task => task.id);
  }, [tasks, lockStates, nestedSprints, statuses]);

  return (
    <ResponsiveToolbar>
      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY ALL 5 VIEWS (same order everywhere)
          Priority 1-3 = most important, hidden last
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* 1-2. Add Mission/Decision (ALL views) - Priority 1 */}
      <ResponsiveToolbar.Item priority={1}>
        <div className="unified-toolbar__group">
          <button
            className="toolbar-btn toolbar-btn--accent"
            onClick={() => onAddTask?.()}
            title="Add new mission"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <MissionIcon />
              </span>
              <span className="toolbar-btn__label">Add Mission</span>
            </span>
          </button>
          
          <button
            className="toolbar-btn"
            onClick={() => onAddDecision?.()}
            title="Add decision node"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <DiamondIcon />
              </span>
              <span className="toolbar-btn__label">Add Decision</span>
            </span>
          </button>
          <button
            className="toolbar-btn"
            onClick={() => onAddWait?.()}
            title="Add a wait-for card"
          >
            <span className="toolbar-btn__content">
              <span className="toolbar-btn__icon">
                <HourglassIcon />
              </span>
              <span className="toolbar-btn__label">Add Wait for</span>
            </span>
          </button>
        </div>
      </ResponsiveToolbar.Item>

      {/* 3. FOKUS Button (ALL views) - Priority 2 */}
      <ResponsiveToolbar.Item priority={2}>
        <FokusButton
          isFocusMode={isFokusMode}
          hasSelection={selectedTaskIds.size > 0}
          selectedCount={selectedTaskIds.size}
          actionableTaskIds={actionableTaskIds}
          onFocusSelected={(ids) => onFokusSelected?.(ids)}
          onExitFocusMode={onExitFokusMode}
          onFilterFocus={onFokusByFilter}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          selectionLabel="Selected Rows"
        />
      </ResponsiveToolbar.Item>

      {/* 4. Filter (ALL views) - Priority 3 */}
      <ResponsiveToolbar.Item priority={3}>
        <KanbanFilterDropdown
          filters={filters}
          onFiltersChange={onFiltersChange}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>

      {/* 5. WIP Limits (ALL views) - Priority 5 */}
      <ResponsiveToolbar.Item priority={5}>
        <StatusLimitsDropdown
          statuses={statuses}
          tasks={tasks}
          onSetWipLimit={onSetWipLimit}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 4 VIEWS (not Flow)
          ═══════════════════════════════════════════════════════════════ */}

      {/* 6. Sort (Kanban, Table, Gantt, Todo) - Priority 6 */}
      <ResponsiveToolbar.Item priority={6}>
        <SortDropdown
          currentSort={sortSetting}
          onSortChange={onSortSettingChange}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>

      {/* 7. Archive Toggle (Kanban, Table, Gantt, Todo) - Priority 8 */}
      <ResponsiveToolbar.Item priority={8}>
        <ToolbarArchiveToggle
          showArchived={showArchived}
          onToggleArchived={onToggleArchived}
          archivedCount={archivedCount}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 3 VIEWS
          ═══════════════════════════════════════════════════════════════ */}

      {/* 8. Swimlanes (Kanban, Table, Gantt, Todo) - Priority 7 */}
      <ResponsiveToolbar.Item priority={7}>
        <SwimlanesDropdown
          currentSwimlane={swimlaneSetting}
          onSwimlaneChange={onSwimlaneSettingChange}
          context={swimlaneContext}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 4 VIEWS (Flow, Kanban, Table, Todo - not Gantt)
          ═══════════════════════════════════════════════════════════════ */}

      {/* 9. Field Visibility (Flow, Kanban, Table, Todo) - Priority 9 */}
      <ResponsiveToolbar.Item priority={9}>
        <FieldVisibilityDropdown
          view="table"
          visibility={columnVisibility}
          onVisibilityChange={onColumnVisibilityChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
        />
      </ResponsiveToolbar.Item>

      {onResetCampaignProgress && (
        <ResponsiveToolbar.Item priority={11} label="Reset campaign">
          <ResetCampaignToolbarItem onResetCampaignProgress={onResetCampaignProgress} />
        </ResponsiveToolbar.Item>
      )}
      {onManageVariables && (
        <ResponsiveToolbar.Item priority={12} label="Variables">
          <VariablesToolbarItem onManageVariables={onManageVariables} />
        </ResponsiveToolbar.Item>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SEARCH (ALL views - always at end)
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* Mission Search - Priority 4 (important, stays visible) */}
      <ResponsiveToolbar.Item priority={4}>
        <button
          className="toolbar-btn"
          onClick={onOpenMissionSearch}
          title="Search missions (Ctrl+F)"
        >
          <span className="toolbar-btn__content">
            <span className="toolbar-btn__icon">
              <SearchIcon />
            </span>
            <span className="toolbar-btn__label">Search</span>
          </span>
        </button>
      </ResponsiveToolbar.Item>
    </ResponsiveToolbar>
  );
};

export default TableToolbar;
